// Hash-based router with page lifecycle
import { renderHome, initHome } from './ui/pages/home';
import { renderPlay, initPlay } from './ui/pages/play';
import { renderLearn, initLearn } from './ui/pages/learn';
import { renderPuzzle, initPuzzle } from './ui/pages/puzzle';
import { renderStats, initStats } from './ui/pages/stats';
import { renderSettings, initSettings } from './ui/pages/settings';
import { renderClock, initClock } from './ui/pages/clock';

type PageRenderer = () => string | Promise<string>;
type PageInit = () => void | Promise<void>;

interface Route {
  render: PageRenderer;
  init: PageInit;
}

const routes: Record<string, Route> = {
  '/': { render: renderHome, init: initHome },
  '/play': { render: renderPlay, init: initPlay },
  '/learn': { render: renderLearn, init: initLearn },
  '/puzzle': { render: renderPuzzle, init: initPuzzle },
  '/stats': { render: renderStats, init: initStats },
  '/settings': { render: renderSettings, init: initSettings },
  '/clock': { render: renderClock, init: initClock },
};

let currentCleanup: (() => void) | null = null;

async function navigate(): Promise<void> {
  const hash = window.location.hash.slice(1) || '/';
  const route = routes[hash];

  if (!route) {
    window.location.hash = '#/';
    return;
  }

  // Update active nav link
  document.querySelectorAll('.nav-links a').forEach(a => {
    const href = a.getAttribute('href');
    if (href === '#' + hash) {
      a.classList.add('active');
    } else {
      a.classList.remove('active');
    }
  });

  // Render page content
  const content = document.getElementById('page-content');
  if (!content) return;

  // Call cleanup if exists
  if (currentCleanup) {
    currentCleanup();
    currentCleanup = null;
  }

  // Render (supports async renderers like stats)
  const html = await route.render();
  content.innerHTML = html;

  // Init page (attach event listeners)
  await route.init();
}

export function initRouter(): void {
  window.addEventListener('hashchange', navigate);
  navigate();
}

export function onNavigate(cleanup: () => void): void {
  currentCleanup = cleanup;
}
